/**
 * Kurdistan landing page (SEO-ready template)
 * - Provides meta + FAQ + Place/LocalBusiness JSON-LD for Kurdistan region.
 * - Template only; keep routing decisions separate.
 */

import React from 'react'
import Seo from '../../components/Seo'
import FAQList from '../../components/faq/FAQList'

/**
 * Kurdistan landing page component
 */
export default function KurdistanLanding(): JSX.Element {
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What markets in Kurdistan does Edicoo cover?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Edicoo covers regional markets across Kurdistan including Erbil and surrounding governorates with culturally adapted campaigns.'
        }
      },
      {
        '@type': 'Question',
        name: 'Does Edicoo run Kurdish-language influencer campaigns?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes — we specialize in Kurdish-language influencer selection, content adaptation and campaign performance tracking.'
        }
      }
    ]
  }

  const placeJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Place',
    name: 'Edicoo Agency — Kurdistan Region',
    url: 'https://your-site.com/landings/kurdistan',
    address: {
      '@type': 'PostalAddress',
      addressRegion: 'Kurdistan',
      addressCountry: 'IQ'
    },
    sameAs: [
      'https://www.linkedin.com/company/edicoo',
      'https://www.instagram.com/edicoo'
    ]
  }

  const faqItems = (faqJsonLd.mainEntity || []).map((m: any) => ({
    question: m.name || '',
    answer: (m.acceptedAnswer && (m.acceptedAnswer.text || '')) || ''
  }))

  return (
    <>
      <Seo
        title="Edicoo — Kurdistan Advertising & Digital Marketing"
        description="Regional Edicoo services in Kurdistan: media planning, influencer marketing and digital campaigns tailored for Kurdish audiences."
        url="https://your-site.com/landings/kurdistan"
        image="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/512dc89b-3d94-4f7e-bef6-93d175317cd5.jpg"
        jsonLd={[faqJsonLd, placeJsonLd]}
      />

      <main className="max-w-4xl mx-auto py-20 px-4">
        <h1 className="text-4xl font-bold mb-4">Edicoo — Kurdistan</h1>
        <p className="text-lg text-gray-700">
          Regional campaigns, Kurdish-language content and influencer marketing across Kurdistan.
        </p>
      </main>

      {/* Visible FAQ section (non-intrusive, matches injected JSON-LD) */}
      <FAQList items={faqItems} />
    </>
  )
}